/**
 * PublicProposalPage — anonymous client-side signing page reached via the
 * 30-day token link auto-generated when a proposal/contract enters the
 * "sent" state.
 *
 * The page:
 *   - Renders the proposal HTML in an iframe (server-rendered)
 *   - Shows two actions: Stampa (open the iframe content in a print-friendly
 *     window) and Carica firmato (multipart upload, max 20 MB, image or pdf)
 *   - On successful upload the page locks itself with a success message,
 *     because the link is now consumed (server-side).
 *   - On expired / already-used / not-found tokens, shows a friendly error
 *     screen without leaking which case it is to a casual visitor.
 */
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle2, FileSignature, Printer, ShieldAlert, Upload, XCircle } from 'lucide-react'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'

/**
 * Trigger the browser's print dialog on the iframe content (so the user
 * gets the rendered proposal, NOT the wrapper page chrome). Falls back to
 * opening the preview URL in a new tab if the iframe isn't ready or its
 * contentWindow is inaccessible (shouldn't happen — same-origin — but
 * defensive in case of pop-up oddities).
 */
function printIframe(iframe: HTMLIFrameElement | null, fallbackUrl: string) {
  const win = iframe?.contentWindow
  if (!win) {
    window.open(fallbackUrl, '_blank')
    return
  }
  try {
    win.focus()
    win.print()
  } catch {
    // Some browsers throw on cross-origin print; open in new tab so the
    // user can still Ctrl+P from there.
    window.open(fallbackUrl, '_blank')
  }
}
import { Button } from '../components/ui/Button'
import { Dialog } from '../components/ui/Dialog'

const SIGNED_MAX_BYTES = 20 * 1024 * 1024
const SIGNED_ACCEPT = 'application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif'

type PublicProposalResp = {
  ok: true
  proposal: {
    id: string
    code: string
    revision: number
    type: 'PRV' | 'CTR'
    title: string
    subtitle: string | null
    clientName: string | null
    status: string
    totalAmountEur: string | null
    vatRate: string
    validUntil: string | null
    expiresAt: string | null
  }
  previewUrl: string
}

type PublicProposalError = {
  ok: false
  reason:
    | 'not_found'
    | 'superseded'
    | 'rejected'
    | 'accepted'
    | 'expired'
    | 'already_used'
    | 'wrong_status'
}

async function fetchPublic(token: string): Promise<PublicProposalResp | PublicProposalError> {
  const res = await fetch(`/api/public/proposals/${encodeURIComponent(token)}`, {
    credentials: 'omit',
  })
  if (res.ok) return (await res.json()) as PublicProposalResp
  try {
    const body = await res.json()
    if (body && typeof body === 'object' && body.reason)
      return { ok: false, reason: body.reason as PublicProposalError['reason'] }
  } catch {
    // fallthrough
  }
  return { ok: false, reason: 'not_found' }
}

export function PublicProposalPage({ token }: { token: string }) {
  const q = useQuery({
    queryKey: ['public-proposal', token],
    queryFn: () => fetchPublic(token),
    retry: false,
    refetchOnWindowFocus: false,
  })
  const [uploadOpen, setUploadOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [uploaded, setUploaded] = useState(false)
  const [rejected, setRejected] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    document.title = 'Documento da firmare · Squadron'
  }, [])

  if (q.isLoading) {
    return <FullScreenMessage title="Caricamento documento…" tone="info" />
  }

  if (!q.data || !q.data.ok) {
    const reason = q.data && !q.data.ok ? q.data.reason : 'not_found'
    return <ErrorScreen reason={reason} />
  }

  const { proposal, previewUrl } = q.data
  const docLabel = proposal.type === 'CTR' ? 'Contratto' : 'Proposta'

  if (uploaded) {
    return (
      <FullScreenMessage
        title="Documento ricevuto, grazie!"
        tone="success"
        description={
          <>
            Abbiamo ricevuto il <strong>{docLabel.toLowerCase()}</strong> firmato. Riceverai una
            comunicazione una volta che lo avremo verificato. Questo link non è più attivo.
          </>
        }
      />
    )
  }

  if (rejected) {
    return (
      <FullScreenMessage
        title="Rifiuto registrato"
        tone="warning"
        description={
          <>
            Abbiamo ricevuto il tuo rifiuto del <strong>{docLabel.toLowerCase()}</strong>. Verrai
            ricontattato per discutere le modifiche necessarie. Questo link non è più attivo.
          </>
        }
      />
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col">
      {/* Top bar */}
      <header className="border-b border-zinc-800 bg-zinc-900/70 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-cyan-300 font-mono">
              {proposal.code} · R{proposal.revision} · {docLabel}
            </div>
            <h1 className="text-base font-semibold text-zinc-50 truncate">{proposal.title}</h1>
            {proposal.clientName && (
              <div className="text-xs text-zinc-500 truncate">Per: {proposal.clientName}</div>
            )}
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => printIframe(iframeRef.current, previewUrl)}
            >
              <Printer className="size-4" /> Stampa
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setRejectOpen(true)}
              className="text-pink-300 hover:bg-pink-500/10 hover:text-pink-200"
            >
              <XCircle className="size-4" /> Rifiuta
            </Button>
            <Button size="sm" onClick={() => setUploadOpen(true)}>
              <FileSignature className="size-4" /> Carica {docLabel.toLowerCase()} firmata
            </Button>
          </div>
        </div>
        {proposal.expiresAt && (
          <div className="max-w-6xl mx-auto px-4 pb-2 text-[11px] text-zinc-500">
            Link valido fino al{' '}
            <span className="text-zinc-300">
              {new Date(proposal.expiresAt).toLocaleString('it-IT')}
            </span>
          </div>
        )}
      </header>

      {/* Iframe preview */}
      <main className="flex-1 min-h-0 p-4 public-doc-main">
        <div className="max-w-6xl mx-auto h-full">
          <iframe
            ref={iframeRef}
            src={previewUrl}
            title={`Anteprima ${proposal.code}`}
            className="w-full h-[calc(100vh-220px)] rounded-md border border-zinc-800 bg-white public-doc-iframe"
          />
        </div>
      </main>

      {/*
       * Print stylesheet for the public signing page:
       * - Hide the wrapper chrome (header bar with buttons, page margins).
       * - Make the iframe occupy the full page so Ctrl+P on the parent
       *   produces the same output as the Stampa button.
       * Note: the iframe itself ALSO carries its own print styles (the
       *   server-rendered HTML has @media print rules); both apply.
       */}
      <style>{`
        @media print {
          html, body { background: #fff !important; }
          header, .public-doc-main { padding: 0 !important; margin: 0 !important; }
          header { display: none !important; }
          .public-doc-main > div { max-width: none !important; }
          .public-doc-iframe {
            border: none !important;
            border-radius: 0 !important;
            width: 100% !important;
            height: auto !important;
            min-height: 100vh;
          }
        }
      `}</style>

      {uploadOpen && (
        <UploadDialog
          token={token}
          docLabel={docLabel}
          onClose={() => setUploadOpen(false)}
          onSuccess={() => {
            setUploadOpen(false)
            setUploaded(true)
          }}
        />
      )}

      {rejectOpen && (
        <RejectDialog
          token={token}
          docLabel={docLabel}
          onClose={() => setRejectOpen(false)}
          onSuccess={() => {
            setRejectOpen(false)
            setRejected(true)
          }}
        />
      )}
    </div>
  )
}

// ============================================================
// Reject dialog — client refuses to sign. Reason is required (min 5 chars).
// ============================================================
function RejectDialog({
  token,
  docLabel,
  onClose,
  onSuccess,
}: {
  token: string
  docLabel: string
  onClose: () => void
  onSuccess: () => void
}) {
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  const reject = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/public/proposals/${encodeURIComponent(token)}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
        credentials: 'omit',
      })
      if (!res.ok) {
        const body = await res.text()
        throw new Error(body || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess,
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  })

  const canSubmit = reason.trim().length >= 5 && !reject.isPending

  return (
    <Dialog open onClose={onClose} title={`Rifiuta ${docLabel.toLowerCase()}`} size="md">
      <div className="p-5 space-y-4">
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200">
          Stai per <strong>rifiutare</strong> il {docLabel.toLowerCase()}. L&apos;operazione è
          definitiva: il link non sarà più valido e verrà notificato il rifiuto. Per ripartire
          servirà una nuova versione del documento.
        </div>

        <div>
          {/* biome-ignore lint/a11y/noLabelWithoutControl: htmlFor binds to the textarea below */}
          <label
            htmlFor="reject-reason"
            className="block text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-1"
          >
            Motivo del rifiuto (richiesto)
          </label>
          <textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Es. richiesta modifica clausola X, importo non concordato, ecc."
            rows={5}
            className="w-full rounded-md border border-zinc-700 bg-zinc-950/60 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-cyan-500/60 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 resize-y"
          />
          <p className="mt-1 text-[11px] text-zinc-500">
            Minimo 5 caratteri. Il messaggio sarà letto da chi ha emesso il documento.
          </p>
        </div>

        {error && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={reject.isPending}>
            Annulla
          </Button>
          <Button
            onClick={() => reject.mutate()}
            disabled={!canSubmit}
            className="bg-pink-500 hover:bg-pink-400 text-white"
          >
            <XCircle className="size-4" />
            {reject.isPending ? 'Invio…' : 'Conferma rifiuto'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ============================================================
// Upload dialog
// ============================================================
function UploadDialog({
  token,
  docLabel,
  onClose,
  onSuccess,
}: {
  token: string
  docLabel: string
  onClose: () => void
  onSuccess: () => void
}) {
  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const upload = useMutation({
    mutationFn: async (f: File) => {
      const fd = new FormData()
      fd.append('file', f)
      const res = await fetch(`/api/public/proposals/${encodeURIComponent(token)}/upload-signed`, {
        method: 'POST',
        body: fd,
        credentials: 'omit',
      })
      if (!res.ok) {
        const body = await res.text()
        throw new Error(body || `HTTP ${res.status}`)
      }
      return res.json()
    },
    onSuccess,
    onError: (err) => setError(err instanceof Error ? err.message : String(err)),
  })

  function pickFile(f: File) {
    setError(null)
    if (f.size > SIGNED_MAX_BYTES) {
      setError(
        `File troppo grande (${formatBytes(f.size)}). Limite: ${formatBytes(SIGNED_MAX_BYTES)}.`,
      )
      return
    }
    if (!SIGNED_ACCEPT.includes(f.type) && f.type !== '') {
      setError(`Tipo non supportato (${f.type}). Usa PDF o immagine.`)
      return
    }
    setFile(f)
  }

  function onInputChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files?.[0]) pickFile(e.target.files[0])
    e.target.value = ''
  }
  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragging(false)
    if (e.dataTransfer.files[0]) pickFile(e.dataTransfer.files[0])
  }

  return (
    <Dialog open onClose={onClose} title={`Carica ${docLabel.toLowerCase()} firmata`} size="md">
      <div className="p-5 space-y-4">
        <p className="text-sm text-zinc-400 leading-relaxed">
          Carica il file firmato (PDF, JPG, PNG, WebP, HEIC). Dopo la conferma il documento verrà
          inoltrato per la verifica e questo link non sarà più attivo.
        </p>

        {/* biome-ignore lint/a11y/useSemanticElements: drop zone is a div */}
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click()
          }}
          onDrop={onDrop}
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={(e) => {
            e.preventDefault()
            setDragging(false)
          }}
          className={`rounded-lg border-2 border-dashed cursor-pointer p-6 text-center transition-all ${
            dragging
              ? 'border-cyan-400 bg-cyan-500/10'
              : file
                ? 'border-emerald-500/40 bg-emerald-500/5'
                : 'border-zinc-700 bg-zinc-950/40 hover:border-cyan-500/50'
          }`}
        >
          {file ? (
            <div className="space-y-1">
              <CheckCircle2 className="mx-auto size-7 text-emerald-300" />
              <div className="text-sm text-zinc-100 font-medium truncate">{file.name}</div>
              <div className="text-xs text-zinc-500">
                {formatBytes(file.size)} · {file.type || 'tipo sconosciuto'}
              </div>
              <div className="text-[11px] text-zinc-500 mt-2">
                Clicca per scegliere un file diverso
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              <Upload
                className={`mx-auto size-7 ${dragging ? 'text-cyan-300' : 'text-zinc-500'}`}
              />
              <div className="text-sm text-zinc-300">
                {dragging ? 'Rilascia per selezionare' : 'Trascina qui o clicca per scegliere'}
              </div>
              <div className="text-xs text-zinc-500">
                PDF / JPG / PNG / WebP / HEIC · max {formatBytes(SIGNED_MAX_BYTES)}
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={SIGNED_ACCEPT}
            className="hidden"
            onChange={onInputChange}
          />
        </div>

        {error && (
          <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={upload.isPending}>
            Annulla
          </Button>
          <Button onClick={() => file && upload.mutate(file)} disabled={!file || upload.isPending}>
            <FileSignature className="size-4" />
            {upload.isPending ? 'Caricamento…' : 'Conferma e invia'}
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

// ============================================================
// Friendly error screen
// ============================================================
function ErrorScreen({
  reason,
}: {
  reason:
    | 'not_found'
    | 'superseded'
    | 'rejected'
    | 'accepted'
    | 'expired'
    | 'already_used'
    | 'wrong_status'
}) {
  const messages: Record<
    typeof reason,
    { title: string; text: string; tone: 'info' | 'success' | 'warning' }
  > = {
    not_found: {
      title: 'Link non valido',
      text: 'Il link che hai aperto non corrisponde ad alcun documento. Controlla di aver copiato correttamente l’URL completo, oppure contattaci.',
      tone: 'warning',
    },
    superseded: {
      title: 'Documento aggiornato',
      text: 'Questa versione del documento è stata sostituita da una nuova revisione. Riceverai (o hai già ricevuto) un nuovo link via email con la versione aggiornata da firmare.',
      tone: 'info',
    },
    rejected: {
      title: 'Documento rifiutato',
      text: 'Il documento è stato rifiutato e questo link non è più attivo. Verrai ricontattato per concordare le modifiche necessarie e ricevere una nuova versione.',
      tone: 'warning',
    },
    accepted: {
      title: 'Documento accettato',
      text: 'Il documento è stato accettato. Non sono richieste ulteriori azioni: questo link non è più attivo.',
      tone: 'success',
    },
    expired: {
      title: 'Link scaduto',
      text: 'Il link di firma è scaduto (validità 30 giorni). Contattaci per riceverne uno nuovo.',
      tone: 'warning',
    },
    already_used: {
      title: 'Documento già caricato',
      text: 'Il documento firmato è già stato ricevuto: questo link non è più attivo. Ti aggiorneremo via email sulla verifica.',
      tone: 'info',
    },
    wrong_status: {
      title: 'Link non più disponibile',
      text: 'Il documento collegato non è più in stato "inviato". Contattaci se hai bisogno di assistenza.',
      tone: 'warning',
    },
  }
  const m = messages[reason]
  return <FullScreenMessage title={m.title} description={m.text} tone={m.tone} />
}

// ============================================================
// Generic full-screen message
// ============================================================
function FullScreenMessage({
  title,
  description,
  tone,
}: {
  title: string
  description?: React.ReactNode
  tone: 'info' | 'success' | 'warning'
}) {
  const Icon = tone === 'success' ? CheckCircle2 : tone === 'warning' ? ShieldAlert : Upload
  const color =
    tone === 'success'
      ? 'text-emerald-300'
      : tone === 'warning'
        ? 'text-amber-300'
        : 'text-cyan-300'
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 grid place-items-center px-4">
      <div className="max-w-md text-center space-y-4">
        <Icon className={`size-12 mx-auto ${color}`} />
        <h1 className="text-xl font-semibold text-zinc-50">{title}</h1>
        {description && <p className="text-sm text-zinc-400 leading-relaxed">{description}</p>}
      </div>
    </div>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(2)} MB`
}
